/**
 * /contact route — GNPC critical "contact + takedown SLA" page.
 *
 * Spec: GTM/GNPC-APPLICATION-KIT.md §3.2.
 */

import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../use-lang';
import { resolveContact } from '../i18n/contact';

export function ContactRoute() {
  const { lang } = useLang();
  const copy = useMemo(() => resolveContact(lang), [lang]);

  useEffect(() => {
    document.title = copy.meta_title;
    setMeta('description', copy.meta_description);
    setMeta('og:title', copy.meta_title, true);
    setMeta('og:description', copy.meta_description, true);
  }, [copy]);

  return (
    <main style={{ maxWidth: 720, margin: '32px auto 96px', padding: '0 16px' }}>
      <h1 className="h-display" style={{ fontSize: 'clamp(28px, 4vw, 40px)', marginTop: 0 }}>
        {copy.page_title}
      </h1>

      <section aria-labelledby="contact-channels-heading">
        <h2 id="contact-channels-heading" className="h-section" style={{ fontSize: 24 }}>
          {copy.channels_heading}
        </h2>
        <p>{copy.channels_intro}</p>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: 'var(--color-card)' }}>
                <th scope="col" style={cellHeader}>{copy.table_purpose}</th>
                <th scope="col" style={cellHeader}>{copy.table_email}</th>
                <th scope="col" style={cellHeader}>{copy.table_sla}</th>
              </tr>
            </thead>
            <tbody>
              {copy.rows.map((r, i) => (
                <tr key={`${r.email}-${i}`}>
                  <td style={cell}>{r.purpose}</td>
                  <td style={cell}>
                    <a href={`mailto:${r.email}`}>{r.email}</a>
                  </td>
                  <td style={cell}>{r.sla}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section aria-labelledby="contact-takedown-heading" style={{ marginTop: 32 }}>
        <h2 id="contact-takedown-heading" className="h-section" style={{ fontSize: 24 }}>
          {copy.takedown_heading}
        </h2>
        <p>{copy.takedown_intro}</p>
        <ol>
          <li>{copy.takedown_step_1}</li>
          <li>{copy.takedown_step_2}</li>
          <li>{copy.takedown_step_3}</li>
        </ol>
        <p>
          <a href="mailto:legal@asklaw.vn?subject=Takedown%20request" className="cta-secondary">
            {copy.takedown_form_label}
          </a>
        </p>
      </section>

      <section aria-labelledby="contact-dsar-heading" style={{ marginTop: 32 }}>
        <h2 id="contact-dsar-heading" className="h-section" style={{ fontSize: 24 }}>
          {copy.dsar_heading}
        </h2>
        <p>{copy.dsar_intro}</p>
        <ul>
          <li>{copy.dsar_right_1}</li>
          <li>{copy.dsar_right_2}</li>
          <li>{copy.dsar_right_3}</li>
          <li>{copy.dsar_right_4}</li>
        </ul>
        <p>
          {copy.dsar_send_to.split('dpo@asklaw.vn')[0]}
          <a href="mailto:dpo@asklaw.vn">dpo@asklaw.vn</a>
          {copy.dsar_send_to.split('dpo@asklaw.vn')[1]?.split(/quản lý|management/)[0] ?? ' '}
          <Link to="/news/digest/manage">{copy.dsar_manage_link}</Link>
          {'.'}
        </p>
      </section>
    </main>
  );
}

const cellHeader: React.CSSProperties = {
  textAlign: 'left',
  padding: '10px 12px',
  borderBottom: '2px solid var(--color-border)',
  fontWeight: 600,
};

const cell: React.CSSProperties = {
  padding: '10px 12px',
  borderBottom: '1px solid var(--color-border)',
  verticalAlign: 'top',
};

function setMeta(name: string, content: string, isProperty = false): void {
  const attr = isProperty ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.content = content;
}
